import { useEffect, useRef, useCallback } from 'react'
import { getFeathersClient } from '../pages/chat/feathers-client'

const HEARTBEAT_INTERVAL_MS = 60000

export function useHeartbeat(params: { enabled: boolean }) {
	const { enabled } = params
	const intervalRef = useRef<NodeJS.Timeout | null>(null)
	const isInForegroundRef = useRef<boolean>(true)

	const updateForegroundState = useCallback(() => {
		const visible = document.visibilityState === 'visible'
		const focused = document.hasFocus?.() ?? true
		isInForegroundRef.current = visible && focused
	}, [])

	const sendHeartbeat = useCallback(async () => {
		if (!enabled)
			return

		try {
			const feathersClient = getFeathersClient()
			await feathersClient.service('heartbeat').create({
				isInForeground: isInForegroundRef.current
			})
		} catch (error) {
			console.error('Failed to send heartbeat:', error)
		}
	}, [enabled])

	useEffect(() => {
		if (!enabled)
			return

		updateForegroundState()

		const handleVisibilityChange = () => {
			updateForegroundState()
			sendHeartbeat()
		}

		const handleFocus = () => {
			updateForegroundState()
			sendHeartbeat()
		}

		const handleBlur = () => {
			updateForegroundState()
			sendHeartbeat()
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)
		window.addEventListener('focus', handleFocus)
		window.addEventListener('blur', handleBlur)

		intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)

		sendHeartbeat()

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('focus', handleFocus)
			window.removeEventListener('blur', handleBlur)

			if (intervalRef.current)
				clearInterval(intervalRef.current)
		}
	}, [enabled, sendHeartbeat, updateForegroundState])
}
